import { getEntityUrl } from './navHelpers';
import dompurify from 'dompurify';
import moment from 'moment';

const sanitizer = dompurify.sanitize;
const markdownRegexGlobal = /@\[([^[]*)]\(([^(^)]*)\)\(([^(^)]*)\)/g;
const markdownRegex = /@\[([^[]*)]\(([^(^)]*)\)\(([^(^)]*)\)/;

const shorten = (string, maxLength, ellipsis = true) => {
  const showEllipsis = string?.length > maxLength && ellipsis;
  return `${string?.substring(0, maxLength)}${showEllipsis ? '…' : ''}`;
};

const shortenRenderedHTML = (htmlString, maxLength, ellipsis = true) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  // const textContent = doc.body.textContent || ''; // Extract plain text from HTML

  // const truncatedText =
  //   textContent.length > maxLength
  //     ? `${textContent.substring(0, maxLength)}${ellipsis ? '…' : ''}`
  //     : textContent;

  // Replace the plain text content with the truncated version while keeping HTML structure intact
  let charCount = 0;
  const truncateNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const remaining = maxLength - charCount;
      if (remaining <= 0) {
        node.textContent = '';
      } else if (charCount + node.textContent.length > maxLength) {
        node.textContent =
          node.textContent.substring(0, remaining) + (ellipsis ? '…' : '');
        charCount = maxLength;
      } else {
        charCount += node.textContent.length;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      [...node.childNodes].forEach(truncateNode);
    }
  };

  const clonedDoc = doc.body.cloneNode(true);
  truncateNode(clonedDoc);

  return clonedDoc.innerHTML;
};

const convertMarkdownToFormatted = (text) => {
  // process markdown-style links
  let convertedText = text.replace(markdownRegexGlobal, (str) => {
    let [_full, username, id, entityType] = str.match(markdownRegex);
    return `<a href="${getEntityUrl({ entityType, id })}">@${username}</a>`;
  });

  // process raw URLs not in <a> tags
  const urlRegex = /(^|\s)((https?:\/\/[^\s<]+)|(\/[^\s<]+))/gi;

  // Split HTML to avoid modifying existing tags
  const parts = convertedText.split(/(<[^>]+>)/g);

  const processedParts = parts.map((part, index) => {
    if (index % 2 === 0) {
      // Only process text nodes
      return part.replace(urlRegex, (match, space, href) => {
        try {
          const url = new URL(href, window.location.href);
          const isExternal = url.hostname !== window.location.hostname;

          const linkAttributes = isExternal
            ? ' target="_blank" rel="noopener noreferrer"'
            : '';

          return `${space}<a href="${href}"${linkAttributes}>${href}</a>`;
        } catch (e) {
          return match;
        }
      });
    }
    return part;
  });

  return processedParts.join('');
};

const convertSnakeToSpaces = (text) =>
  text
    .split('_')
    .filter((x) => x.length > 0)
    .join(' ');

const getLedgerUrl = (id, entityType) => {
  if (entityType === 'CHARITY') {
    return `/admin/charity/${id}`;
  } else if (entityType === 'COMPANY') {
    return `/admin/company/${id}/activity`;
  } else {
    return `/admin/user/${id}/activity`;
  }
};
const getYourCauzesUrl = (id, entityType) => {
  if (entityType === 'USER') {
    return `/admin/user/${id}/cauzes`;
  } else if (entityType === 'COMPANY') {
    return `/admin/company/${id}/cauzes`;
  } else if (entityType === 'CHARITY') {
    return `/admin/charity/${id}/cauzes`;
  } else if (entityType === 'INFLUENCER') {
    return `/admin/influencer/${id}/cauzes`;
  } else {
    return '/cauzes';
  }
};
const getProfileUrl = (id, entityType) => {
  if (!id || !entityType) {
    return;
  }
  if (entityType === 'COMPANY') {
    return `/company/${id}`;
  } else if (entityType === 'CHARITY') {
    return `/charity/${id}`;
  } else {
    return `/profile/${id}`;
  }
};

const getQueryParam = (search, key) => {
  const params = new URLSearchParams(search);
  return params.get(key);
};

const showEndedText = (cauze) => {
  if (moment(cauze.endDate).isBefore(moment())) {
    return true;
  }

  if (cauze.eventState === 'INACTIVE') {
    return true;
  }
  return false;
};
/**
 * Capitalizes a street address properly, handling abbreviations and common patterns
 * Examples:
 *   "123 n main st apt 4b" -> "123 N Main St Apt 4B"
 *   "456 south park avenue" -> "456 South Park Avenue"
 *   "789 w. elm blvd suite 200" -> "789 W. Elm Blvd Ste 200"
 * @param {string} address - The street address to capitalize
 * @returns {string} - The capitalized street address
 */
const capitalizeStreetAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return address;
  }

  // Common street type abbreviations (always uppercase)
  const streetTypes = {
    st: 'St',
    street: 'St',
    ave: 'Ave',
    avenue: 'Ave',
    blvd: 'Blvd',
    boulevard: 'Blvd',
    rd: 'Rd',
    road: 'Rd',
    dr: 'Dr',
    drive: 'Dr',
    ln: 'Ln',
    lane: 'Ln',
    ct: 'Ct',
    court: 'Ct',
    pl: 'Pl',
    place: 'Pl',
    pkwy: 'Pkwy',
    parkway: 'Pkwy',
    way: 'Way',
    circle: 'Cir',
    cir: 'Cir',
    hwy: 'Hwy',
    highway: 'Hwy',
    trl: 'Trl',
    trail: 'Trl',
  };

  // Directional abbreviations (always uppercase)
  const directionals = {
    n: 'N',
    north: 'N',
    s: 'S',
    south: 'S',
    e: 'E',
    east: 'E',
    w: 'W',
    west: 'W',
    ne: 'NE',
    northeast: 'NE',
    nw: 'NW',
    northwest: 'NW',
    se: 'SE',
    southeast: 'SE',
    sw: 'SW',
    southwest: 'SW',
  };

  // Unit designators (always uppercase)
  const unitDesignators = {
    apt: 'Apt',
    apartment: 'Apt',
    ste: 'Ste',
    suite: 'Ste',
    unit: 'Unit',
    '#': '#',
    number: '#',
    bldg: 'Bldg',
    building: 'Bldg',
    fl: 'Fl',
    floor: 'Fl',
  };

  // Split address into words, preserving spaces
  const trimmedAddress = address.trim();
  const words = trimmedAddress.split(/\s+/);

  // Find the position of the street type (usually last meaningful word)
  let streetTypeIndex = -1;
  for (let i = words.length - 1; i >= 0; i--) {
    const cleanWord = words[i].toLowerCase().replace(/[.,;:]/g, '');
    if (streetTypes[cleanWord]) {
      streetTypeIndex = i;
      break;
    }
  }

  // Process each word
  const processedWords = words.map((word, index) => {
    // Preserve punctuation
    const cleanWord = word.replace(/[.,;:]/g, '').toLowerCase();
    const punctuation = word.match(/[.,;:]/g)?.join('') || '';

    // Handle unit designators (words after street type)
    if (streetTypeIndex >= 0 && index > streetTypeIndex) {
      if (unitDesignators[cleanWord]) {
        return unitDesignators[cleanWord] + punctuation;
      }
      // Handle unit numbers (like "4B", "200")
      if (/^[a-z]?\d+[a-z]?$/i.test(cleanWord)) {
        return cleanWord.toUpperCase() + punctuation;
      }
    }

    // Handle street types
    if (index === streetTypeIndex && streetTypes[cleanWord]) {
      return streetTypes[cleanWord] + punctuation;
    }

    // Handle directional prefixes (first word) or suffixes (word before street type)
    if (
      (index === 0 || index === streetTypeIndex - 1) &&
      directionals[cleanWord]
    ) {
      return directionals[cleanWord] + punctuation;
    }

    // Handle cardinal directions in full form (capitalize normally)
    if (
      ['north', 'south', 'east', 'west', 'northeast', 'northwest', 'southeast', 'southwest'].includes(
        cleanWord,
      )
    ) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }

    // Handle pure numbers (house numbers, etc.) - keep as is
    if (/^\d+$/.test(word)) {
      return word;
    }

    // Handle alphanumeric (like apartment numbers "4B")
    if (/^[a-z]?\d+[a-z]?$/i.test(word)) {
      return word.toUpperCase();
    }

    // Default: capitalize first letter, lowercase the rest
    if (word.length === 0) {
      return word;
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return processedWords.join(' ');
};

export {
  shortenRenderedHTML,
  getProfileUrl,
  getQueryParam,
  getLedgerUrl,
  showEndedText,
  getYourCauzesUrl,
  shorten,
  convertMarkdownToFormatted,
  convertSnakeToSpaces,
  capitalizeStreetAddress,
  markdownRegex,
  sanitizer,
};
export const linkify = require('linkifyjs');
